import { useMemo } from "react";
import { COURSE_TYPES_PATH, DESSERT_DAYS_PATH, SCHOOLS_PATH, SIDE_DAYS_PATH } from "../../../functions/src/admin/paths";
import { getAllMenuWeekDaysOverlappingCalendarMonth, getMenuDayKeysRangeBounds } from "../../../functions/src/api/dates";
import type { DataSnapshot } from "../../api/firebase";
import { useDatabase, useList, type DatabaseFilter } from "../../api/firebaseHooks";

export function getCalendarMonthPrintDateRange(monthDate: Date): { dateStart: Date; dateEnd: Date } {
    return {
        dateStart: new Date(monthDate.getFullYear(), monthDate.getMonth(), 1),
        dateEnd: new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23),
    };
}

export function getMenuWeekDaysForCalendarMonth(dateStart: Date): string[] {
    return getAllMenuWeekDaysOverlappingCalendarMonth(dateStart.getFullYear(), dateStart.getMonth());
}

/** Bounded RTDB query for menu day nodes (`yyyy_MM_dd`), shared by admin menu editor and order-app print. */
export function menuDaysKeysRangeFilterForWeekDays(days: string[]): DatabaseFilter {
    const bounds = getMenuDayKeysRangeBounds(days);
    if (!bounds) {
        return (ref) => ref.limitToFirst(0);
    }
    return (ref) => ref.orderByKey().startAfter(bounds.startAfterKey).endBefore(bounds.endBeforeKey);
}

export function menuListToMenuData(menuList: DataSnapshot[]): Record<string, unknown> | null {
    if (!menuList?.length) {
        return null;
    }
    const result: Record<string, unknown> = {};
    menuList.forEach((snap) => {
        if (snap.key) {
            result[snap.key] = snap.val();
        }
    });
    return Object.keys(result).length ? result : null;
}

/** Published menu days for one calendar month print (includes Mon–Fri from overlapping weeks). */
export function usePublishedMenuDataForPrintMonth(menuPath: string, dateStart: Date): Record<string, unknown> | null {
    const printWeekDays = useMemo(
        () => getMenuWeekDaysForCalendarMonth(dateStart),
        [dateStart.getFullYear(), dateStart.getMonth()]
    );
    const menuFilter = useMemo(
        () => menuDaysKeysRangeFilterForWeekDays(printWeekDays),
        [printWeekDays]
    );
    const menuSnapshot = useDatabase(menuPath, menuFilter, true);
    const menuList = useList(menuSnapshot);
    return useMemo(() => menuListToMenuData(menuList), [menuList]);
}

export type MonthlyMenuPrintSupportSnapshots = {
    sideDaysSnapshot: DataSnapshot | undefined;
    dessertDaysSnapshot: DataSnapshot | undefined;
    courseTypesSnapshot: DataSnapshot | undefined;
    schoolSnapshot: DataSnapshot | undefined;
};

export function useMonthlyMenuPrintSupportSnapshots(schoolKey: string | null | undefined): MonthlyMenuPrintSupportSnapshots {
    const sideDaysSnapshot = useDatabase(SIDE_DAYS_PATH);
    const dessertDaysSnapshot = useDatabase(DESSERT_DAYS_PATH);
    const courseTypesSnapshot = useDatabase(COURSE_TYPES_PATH);
    const schoolSnapshot = useDatabase(schoolKey ? `${SCHOOLS_PATH}/${schoolKey}` : null);
    return { sideDaysSnapshot, dessertDaysSnapshot, courseTypesSnapshot, schoolSnapshot };
}

export function monthlyMenuPrintSupportDataFromSnapshots(snapshots: MonthlyMenuPrintSupportSnapshots) {
    return {
        sideDaysData: snapshots.sideDaysSnapshot?.val() ?? null,
        dessertDaysData: snapshots.dessertDaysSnapshot?.val() ?? null,
        schoolData: snapshots.schoolSnapshot?.val() ?? null,
        courseTypesData: snapshots.courseTypesSnapshot?.val() ?? null,
    };
}
